/**
 * SessionBlockCard — the web session surface's renderer for a `session_block`.
 *
 * A native-ir turn is not one document. The model authors typed DATA between
 * `<vi-block>…</vi-block>`, the api-server's WAL reducer projects each envelope
 * into ONE card `{template:"session_block", data:{node:{type,id,data}}}`, and
 * the client runs that node through the SAME ViewIR interpreter the native
 * widgets use — as a NODE, never as a page. There is no HTML anywhere on this
 * path: no iframe, no Handlebars, no model-authored CSS.
 *
 * THREE THINGS THIS FILE OWNS, and none of them belong in a block:
 *
 *  1. THE SURFACE'S TOKENS. `buildSessionTokenStyle()` puts the session's
 *     `--vi-*` set on one root — no accent, no display face, paper and ink.
 *     A block reads tokens; it never decides what they are. This root adds NO
 *     box and NO gutter of its own: a native-ir turn is one answer, so it sits
 *     in the reading column the surface already draws (`SessionView` /
 *     `PlaygroundView` wrap every card in it) exactly like the html lane and
 *     like a plain paragraph — no plate, no second inset, no indent step.
 *  2. THE DEGRADE. An unknown block type is NOT an error: the catalog grows
 *     additively and the api-server deliberately does not validate the type
 *     against a catalog (`session_wal_reducer._emit_block_card`), so a block
 *     from a newer agent reaches an older client by design. It degrades to
 *     `markdown` — the catalog's declared degrade target — so the words
 *     survive instead of the answer vanishing.
 *  3. THE HOST OPS. Opening a picture full-screen, opening a URL, saving a
 *     file: every one is a HOST capability reached through a declared signal.
 *     The full-screen viewer is drawn here, by the surface, exactly as
 *     `image_gallery.sealed` requires — the block never draws its own.
 *
 * FIRE-AND-CONTINUE NEEDS NOTHING HERE. A still-generating asset arrives as
 * DATA (`slot` and no ref; `error` when it failed) and the resolved ref arrives
 * as a `replace_card` — a new `data.node`, so the block simply re-renders with
 * the picture in it. The `setSrc`/`setItems` slot inboxes stay unused on this
 * surface: the server re-emits the whole node rather than pushing into a card
 * that has already rendered, which is the one path that survives a reload.
 *
 * P2, deliberately: the FIRST hero of a turn bleeds edge-to-edge
 * (`variant: "bleed"`), which needs the turn's block ordering this renderer is
 * not handed. When it lands it also needs the SURFACE's gutter to cancel, not
 * `--vi-gutter` — this root stopped drawing one.
 */
import { useCallback, useMemo, useState } from 'react';
import { ViewIRComposition } from '../widget-native/viewir/interpreter';
import KitKeyframes from '../widget-native/KitKeyframes';
import { lookupKit } from '../widget-native/viewir/registry';
import { buildSessionTokenStyle } from '../widget-native/viewir/tokens';
import { ACTION_HOST_OP } from '../widget-native/viewir/sessionBlockVocab';

/**
 * The blocks whose `tapped` means "open this picture", not "play this".
 *
 * `shopping_list` and `table` are here for their THUMBNAIL column (B18 P-A):
 * the picture is an external CDN address the answer quotes, and enlarging it is
 * the same host verb a gallery tap asks for. Their OTHER tap — the row that
 * leads to a shop — is a different signal (`activated`), so the two never race.
 */
const OPENS_VIEWER = new Set([
  'hero_media', 'before_after', 'image_gallery', 'shopping_list', 'table',
]);

/**
 * …and the one whose `tapped` means "preview this file". The web host's preview
 * IS the browser's: a signed pdf / csv / txt opens in its own tab, and a type
 * the browser cannot render is handed to the download it already knows how to
 * do. Either way LEAVING IS THE HOST'S — the block never navigates.
 */
const OPENS_FILE = new Set(['file_card']);

/** The `kind` of the deliverable a `worker_delegate` signal names by index. */
function deliverableKind(card, index) {
  const items = card?.data?.node?.data?.deliverables;
  const entry = Array.isArray(items) ? items[index] : null;
  return entry && typeof entry === 'object' ? entry.kind : null;
}

/**
 * A field that holds an ADDRESS (a URL or a `media://` handle), which must
 * never be read out as prose.
 *
 * Both ends are checked: a key ending in `url` (`imageUrl`, `beforeImageUrl`,
 * `buyUrl`, `faviconUrl`) and a value that is itself an address.
 */
function isAddressField(key, value) {
  if (String(key).toLowerCase().endsWith('url')) return true;
  if (typeof value !== 'string') return false;
  const v = value.trim().toLowerCase();
  return v.startsWith('media://') || v.startsWith('http://') || v.startsWith('https://');
}

/**
 * `data.node` → a node this build can actually draw.
 *
 * A type the kit knows passes through untouched. Anything else becomes a
 * `markdown` node built from whatever prose the unknown block carried, in the
 * order a block is likely to hold it — its own paragraphs, a body string, or
 * the string fields it happens to have. That is the difference between an
 * additive catalog and a broken card.
 *
 * ⚠️ ADDRESS FIELDS ARE SKIPPED (`isAddressField`). This degrade is the systemic
 * leak on the additive path: a media block newer than this build (`before_after`
 * is the first of them) would otherwise have its `media://…` handles and signed
 * picture URLs printed as BODY TEXT — ugly, and a raw-address leak in front of a
 * human. Skipping them leaves the block's real prose (`title` / `subtitle` /
 * `error` / a description), and a block that has nothing but addresses degrades
 * to its heading alone — a title beats a wall of handles.
 */
function renderableNode(node) {
  if (!node || typeof node !== 'object' || !node.type) return null;
  const data = node.data && typeof node.data === 'object' ? node.data : {};
  if (lookupKit(node.type)) return { type: node.type, props: data };

  const readable = (k, v) => typeof v === 'string' && v && !isAddressField(k, v);
  const paragraphs = Array.isArray(data.paragraphs) && data.paragraphs.length
    ? data.paragraphs.map(String)
    : [['content', data.content], ['text', data.text], ['body', data.body]]
      .filter(([k, v]) => readable(k, v))
      .map(([, v]) => v)
      .concat(
        Object.entries(data)
          .filter(([k, v]) => readable(k, v) && !['title', 'content', 'text', 'body'].includes(k))
          .map(([, v]) => v),
      );
  return {
    type: 'markdown',
    props: {
      title: typeof data.title === 'string' ? data.title : undefined,
      paragraphs: paragraphs.filter(Boolean),
      footer: data.footer,
    },
  };
}

/** The host's full-screen picture viewer — the one `imageTap` opens. */
function ImageViewer({ src, onClose }) {
  if (!src) return null;
  return (
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 60,
        background: 'rgba(0,0,0,0.92)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <img src={src} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
    </div>
  );
}

export default function SessionBlockCard({ card, onCardAction, onFollowUp }) {
  const [viewing, setViewing] = useState(null);
  const node = useMemo(() => renderableNode(card?.data?.node), [card]);
  const style = useMemo(() => buildSessionTokenStyle({ appearance: 'light' }), []);
  const cardId = card?.cardId || card?.id;
  const type = card?.data?.node?.type;

  /**
   * A block's SIGNAL → a host op. Every one of them is reported upward with
   * the card it fired on (the surface's own action channel, which is also what
   * the server's card-action log reads); the two the web host can honour
   * locally — a picture and a URL — are honoured here as well.
   */
  const emit = useCallback((signal, payload = {}) => {
    const send = (action, extra = {}) => onCardAction?.({ action, cardId, ...extra });
    // a block never starts a turn itself: it hands the words to the surface's
    // composer, which is the one thing that can
    const followUp = (text) => {
      const words = typeof text === 'string' ? text.trim() : '';
      if (words) onFollowUp?.(words);
    };

    if (signal === 'tapped' && OPENS_VIEWER.has(type)) {
      if (payload.src) setViewing(payload.src);
      send('open_image', { url: payload.src });
      return;
    }
    // A worker delegate carries MIXED deliverables, so the block type cannot say
    // what a tap means — the DELIVERABLE's kind does. The signal's payload is the
    // catalog's `{index,src}`, so the kind is read off the node this component is
    // already holding rather than added to the wire.
    if (signal === 'tapped' && type === 'worker_delegate' && payload.src) {
      const kind = String(deliverableKind(card, payload.index) || '').toLowerCase();
      if (kind === 'image') {
        setViewing(payload.src);
        send('open_image', { url: payload.src });
      } else if (kind === 'video') {
        // the video plays in place, exactly as `video_player` does
        send('block_tapped', payload);
      } else {
        send('open_url', { url: payload.src });
      }
      return;
    }
    if (signal === 'tapped' && OPENS_FILE.has(type) && payload.src) {
      // the file's own address — already signed/resolved by the api-server, so
      // the host opens exactly what the card is showing
      send('open_url', { url: payload.src });
      return;
    }
    if (signal === 'saveRequested') {
      // writing a file to the device is the host's — App.jsx fetches the blob
      // and hands it to the browser
      send('download_file', {
        url: payload.url,
        ...(payload.filename ? { filename: payload.filename } : {}),
      });
      return;
    }
    if (signal === 'activated') {
      // a link / a shop row: leaving the app is a host capability
      if (payload.url) { send('open_url', { url: payload.url }); return; }
      // an action bar: the closed enum decides which host op is meant.
      const op = ACTION_HOST_OP[payload.action];
      if (op) {
        // the HOST OP is the action this surface reports; the block's own enum
        // rides along under its own key rather than overwriting it
        send(`block_${op}`, { index: payload.index, label: payload.label, block_action: payload.action });
        // `openSession` from a block carries NO session_id — the signal's
        // payload is the closed `{action,index,label}` — so on this surface it
        // means START A TURN, and the turn a button asks for is its own label.
        // The composer is the only thing that can start one, and it is optional
        // (App.jsx renders the live session without one), so a host that has no
        // composer simply reports the tap.
        if (op === 'openSession') followUp(payload.label);
        // TODO(P2-followup): `reloadData` needs a re-dispatch of the turn and
        // `share` needs the session's share token (the playground header
        // prefetches one); neither exists on this surface, so both are reported
        // to the server's card-action log and nothing is claimed locally.
        return;
      }
      // a chip: a follow-up affordance with no host op of its own — the chip's
      // own words are the next prompt.
      send('block_chip', payload);
      followUp(payload.value);
      return;
    }
    send(`block_${signal}`, payload);
  }, [onCardAction, onFollowUp, cardId, type, card]);

  if (!node) return null;

  return (
    <div style={{ ...style, padding: 0 }} data-session-block={type || 'unknown'}>
      {/* the kit's keyframes (the shimmer a still-generating asset waits under);
          mounting them more than once is harmless — identical rules */}
      <KitKeyframes />
      <ViewIRComposition
        type={node.type}
        nodeId={`block-${cardId || node.type}`}
        def={{ composition: node }}
        props={{}}
        ctx={{}}
        emit={emit}
      />
      <ImageViewer src={viewing} onClose={() => setViewing(null)} />
    </div>
  );
}
